import { useState, useEffect } from "react";
import { Nav } from "./components/Nav";
import { Hero } from "./components/Hero";
import { Marquee } from "./components/Marquee";
import { Pipeline } from "./components/Manifesto";
import { Trabajo } from "./components/Trabajo";
import { Studio } from "./components/Studio";
import { Galeria } from "./components/Galeria";
import { Precios } from "./components/Precios";
import { AuthPanel } from "./components/AuthPanel";
import { Legal } from "./components/Legal";
import { Footer } from "./components/Footer";
import { CreditsWallet } from "./components/CreditsWallet";
import { ScrollProgress } from "./components/ScrollProgress";
import { GridBackground } from "./components/GridBackground";
import { useLenis } from "./hooks/useLenis";
import { useCursor } from "./hooks/useCursor";
import { PaymentSuccess, PaymentCancel } from "./components/PaymentPages";
import { getJson, WEBHOOKS } from "./lib/webhooks";

function App() {
  const [authOpen, setAuthOpen] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [credits, setCredits] = useState(null); // null = unknown / error state
  const [gallery, setGallery] = useState([]);
  const [ready, setReady] = useState(false);
  const [authError, setAuthError] = useState("");
  const [route, setRoute] = useState(() => window.location.pathname);

  useLenis();
  useCursor();

  // Emergent OAuth callback: detect #session_id in URL, exchange, then clean the fragment.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

  // Restore session from localStorage
  useEffect(() => {
    try {
      const raw = localStorage.getItem("nexeron_session");

      if (raw) {
        const s = JSON.parse(raw);  

        setUser(s.user);
        setToken(s.token);
        setRefreshToken(s.refreshToken || "");

        if (s.token) {
          localStorage.setItem(
            "token",
            s.token
          );
        }

        if (s.refreshToken) {
          localStorage.setItem(
            "refresh_token",
            s.refreshToken
          );
        }

        if (s.token) {
          getJson(WEBHOOKS.me, s.token)
            .then(res => {
              const me = Array.isArray(res)
                ? res[0]
                : (res.data ?? res);

              const c = Number(me?.credits);

              if (!isNaN(c)) {
                setCredits(c);

                if (me?.user) {
                  setUser(me.user);
                }

              } else {
                setCredits(null);
              }
            })
            .catch(() => {
              setCredits(null);
            })
            .finally(() => {
              setReady(true); // 🔥 ESTE ERA EL PROBLEMA
            });

        } else {
          setReady(true);
        }

      } else {
        setReady(true);
      }

    } catch (_err) {
      setReady(true);
    }
}, []);

  // Load gallery for the active user
  useEffect(() => {
    if (!user?.email) {
      setGallery([]);
      return;
    }
    try {
      const raw = localStorage.getItem(`nexeron_gallery_${user.email}`);
      setGallery(raw ? JSON.parse(raw) : []);
    } catch (_err) {
      setGallery([]);
    }
  }, [user?.email]);

  useEffect(() => {
    if (user) {

      localStorage.setItem(
        "nexeron_session",
        JSON.stringify({
          user,
          token,
          refreshToken,
          credits
        })
      );

    }
  }, [user, token, refreshToken, credits]);

  // Persist gallery per user
  useEffect(() => {
    if (user?.email) {
      localStorage.setItem(
        `nexeron_gallery_${user.email}`,
        JSON.stringify(gallery)
      );
    }
  }, [gallery, user?.email]);

  useEffect(() => {

    const updateToken = () => {

      const session = JSON.parse(
        localStorage.getItem("nexeron_session")
      );

      if (session?.token) {
        setToken(session.token);
        console.log("🔄 Token actualizado en React:", session.token);
      }

    };


    window.addEventListener(
      "token-refreshed",
      updateToken
    );


    return () => {
      window.removeEventListener(
        "token-refreshed",
        updateToken
      );
    };

  }, []);

  const openAuth = () => setAuthOpen(true);
  const closeAuth = () => setAuthOpen(false);

  const onAuth = async (u, t, refreshToken, initialCredits) => {
    
    setUser(u);
    setToken(t);
    setRefreshToken(refreshToken);
    setCredits(initialCredits);

    localStorage.setItem("token", t);

    localStorage.setItem(
      "refresh_token",
      refreshToken
    );

    setRefreshToken(refreshToken);

    localStorage.setItem(
      "nexeron_session",
      JSON.stringify({
        user: u,
        token: t,
        refreshToken,
        credits: initialCredits
      })
    );


    try {

      const res = await getJson(WEBHOOKS.me, t);


      const me = Array.isArray(res)
        ? res[0]
        : (res.data ?? res);


      const c = Number(me?.credits);


      if (!isNaN(c)) {
        setCredits(c);
      }


      if(me?.user){
        setUser(me.user);
      }


    } catch(e){

      console.error("Error cargando usuario",e);

    }


    setTimeout(() => setAuthOpen(false),500);
  };

  const onLogout = () => {

    localStorage.removeItem("nexeron_session");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("token");


    setUser(null);
    setToken("");
    setCredits(null);

    setAuthOpen(false);

    window.location.reload();
  };

  const onGenerated = (item) => {
    if (item?.error || item?.success === false) {
      if (typeof item?.credits === "number") {
        setCredits(item.credits);
      }
      return;
    }

    if (!item?.imageUrl && !item?.url) {
      return;
    }

    setGallery((g) => [item, ...g]);

    if (Number.isFinite(item?.newCredits)) {
      setCredits(item.newCredits);
    }
  };

  const launchStudio = () => {
    const el = document.getElementById("studio");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const onCreditsGranted = async (delta) => {
    try {
      if (!token) return;

      const res = await getJson(WEBHOOKS.me, token);

      const me = Array.isArray(res)
        ? res[0]
        : (res.data ?? res);

      const c = Number(me?.credits);

      if (!isNaN(c)) {
        setCredits(c);
        return;
      }

      if (me?.simulated) {
        setCredits(prev => (typeof prev === "number" ? prev : 0) + (delta || 0));
        return;
      }

      setCredits(null);

    } catch {
      setCredits(null);
    }
  };
  const returnHome = () => {
    setRoute("/");
    setTimeout(() => {
      const el = document.getElementById("studio");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  if (route.startsWith("/payment/success")) {
    return (
      <div className="App grain" data-testid="app-root">
        <PaymentSuccess onCreditsGranted={onCreditsGranted} onDone={returnHome} />
      </div>
    );
  }
  if (route.startsWith("/payment/cancel")) {
    return (
      <div className="App grain" data-testid="app-root">
        <PaymentCancel onDone={returnHome} />
      </div>
    );
  }

  if (!ready) {
    return <div>Cargando...</div>;
  }
  return (
    <div className="App grain relative" data-testid="app-root">
      <GridBackground />
      <div className="tech-grid-fixed" aria-hidden="true" />
      <ScrollProgress />
      <Nav onOpenAuth={openAuth} user={user} credits={credits} />

      <main className={ready ? "opacity-100 transition-opacity duration-700" : "opacity-0"}>
        <Hero onLaunch={launchStudio} />
        <Marquee />
        <Pipeline />
        <Trabajo />
         <Studio user={user} token={token} credits={credits} onNeedAuth={openAuth} onGenerated={onGenerated} />
        <Galeria items={gallery} user={user} onNeedAuth={openAuth} onLaunch={launchStudio} />
        <Precios user={user} onNeedAuth={openAuth} />
        <Legal />
        <Footer />
      </main>

      <AuthPanel
        open={authOpen}
        onClose={closeAuth}
        onAuth={onAuth}
        user={user}
        credits={credits}
        onLogout={onLogout}
        initialError={authError}
      />

      <CreditsWallet
        user={user}
        credits={credits}
        onOpen={() => {
          const el = document.getElementById("precios");
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }}
      />
    </div>
  );
}

export default App;
